type TriggerCreateOutboundSMS = (arg: {
  callerID: string | null;
  message?: string;
  orgName?: string;
  custom?: Record<string, unknown>;
}) => Promise<{
  data?: { conversation_id?: string };
  error?: { status?: number };
}>;

type TriggerCreateConversation = (arg: {
  callerID: string | null;
  orgName?: string;
  startMessage?: string;
}) => Promise<{
  isSuccess: boolean;
  data?: { conversation_id?: string };
}>;

type TriggerGetConversationMetaData = (arg: {
  callerID: string;
  orgName?: string;
  custom?: Record<string, unknown>;
}) => Promise<{
  isSuccess: boolean;
  data?: unknown;
}>;

export interface OutboundSmsConversationDeps {
  callerID: string | null;
  orgName: string | undefined;
  triggerCreateOutboundSMSConversation: TriggerCreateOutboundSMS;
  triggerGetConversationMetaData: TriggerGetConversationMetaData;
}

export interface LocationConversationDeps {
  callerID: string | null;
  orgName: string | undefined;
  triggerCreateConversation: TriggerCreateConversation;
}

export interface StartConversationDeps
  extends OutboundSmsConversationDeps,
    LocationConversationDeps {
  hasLocationsToRender: boolean;
}

export interface StartConversationResult {
  isSuccess: boolean;
  data?: { conversation_id?: string };
}

/**
 * Outbound-SMS creation channel — used when the caller has no renderable
 * locations. This is a pure "create" channel: it does NOT check whether a
 * conversation already exists. Its only caller, `getOrStartConversation` in
 * useTF911Conversation, runs the slice-backed reuse guard first and invokes
 * this only on a cache miss — i.e. when no active conversation was found in the
 * TF911 slice / RTK Query cache.
 *
 * Because of that guard, a 409 ("session already exists") is NOT an expected
 * outcome — it is a fallback for the rare race where the conversation exists
 * server-side but this client's slice is momentarily stale (e.g. it was started
 * on another workstation and that conversation.start websocket hasn't arrived
 * here yet). In that case we recover the existing conversation's id from the
 * metadata so the immediate caller (e.g. live video) can act on it; the
 * websocket then reconciles the slice as usual.
 */
export async function createOutboundSmsConversation(
  message: string | undefined,
  deps: OutboundSmsConversationDeps,
): Promise<StartConversationResult> {
  const {
    callerID,
    orgName,
    triggerCreateOutboundSMSConversation,
    triggerGetConversationMetaData,
  } = deps;

  const response = await triggerCreateOutboundSMSConversation({
    callerID,
    message,
    orgName,
    custom: {},
  });

  if (response?.data?.conversation_id) {
    return {
      isSuccess: true,
      data: { conversation_id: response.data.conversation_id },
    };
  }

  if (response.error?.status === 409) {
    // Fallback only — the reuse guard already ruled out a known conversation,
    // so reaching here means the slice was momentarily stale. Recover the
    // existing session's id from the API rather than failing the start.
    const retryResponse = await triggerGetConversationMetaData({
      callerID: callerID ?? '',
      orgName,
      custom: { isOutboundSMS: true },
    });
    if (retryResponse?.isSuccess) {
      const conversationId = (
        retryResponse.data as { conversation_id?: string } | undefined
      )?.conversation_id;
      return {
        isSuccess: true,
        ...(conversationId
          ? { data: { conversation_id: conversationId } }
          : {}),
      };
    }
  }

  return { isSuccess: false };
}

/**
 * Location channel: used when the caller has renderable locations. Creates the
 * conversation through the standard create endpoint.
 */
export async function createLocationConversation(
  message: string | undefined,
  deps: LocationConversationDeps,
): Promise<StartConversationResult> {
  const { callerID, orgName, triggerCreateConversation } = deps;

  const response = await triggerCreateConversation({
    callerID,
    orgName,
    startMessage: message,
  });

  return {
    isSuccess: response.isSuccess,
    data: response.data,
  };
}

/**
 * Dispatches to the correct creation channel based on whether the caller has
 * renderable locations. This decides only *how* to create a conversation — it
 * assumes the caller has already confirmed none exists. The existence check
 * (TF911 slice / RTK Query cache) lives in getOrStartConversation in
 * useTF911Conversation; these channels are reached only on a cache miss.
 */
export async function startConversation(
  message: string | undefined,
  deps: StartConversationDeps,
): Promise<StartConversationResult> {
  if (deps.hasLocationsToRender) {
    return createLocationConversation(message, deps);
  }

  return createOutboundSmsConversation(message, deps);
}
